import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { Button } from "../common";
import { stripeConnectAPI, type StripeConnectStatus } from "../../lib/api";
import styles from "./StripeConnectSection.module.css";

interface Props {
  organizationId: string;
}

export function StripeConnectSection({ organizationId }: Props) {
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const result = await stripeConnectAPI.getStatus(organizationId);
      setStatus(result);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  async function handleConnect() {
    setConnecting(true);
    try {
      const currentUrl = window.location.href;
      const result = await stripeConnectAPI.authorize(organizationId, currentUrl, currentUrl);
      window.location.href = result.url;
    } catch {
      toast.error("Failed to start Stripe onboarding");
      setConnecting(false);
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true);
    try {
      await stripeConnectAPI.disconnect(organizationId);
      toast.success("Stripe account disconnected");
      setShowDisconnectConfirm(false);
      await fetchStatus();
    } catch {
      toast.error("Failed to disconnect Stripe account");
    } finally {
      setDisconnecting(false);
    }
  }

  if (loading) {
    return (
      <div className={styles.section}>
        <h2>Stripe Connect</h2>
        <p className={styles.muted}>Loading...</p>
      </div>
    );
  }

  if (!status) {
    return (
      <div className={styles.section}>
        <h2>Stripe Connect</h2>
        <p className={styles.muted}>Billing is not configured for this instance.</p>
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className={styles.section}>
        <h2>Stripe Connect</h2>
        <p className={styles.description}>
          Connect your Stripe account to accept payments for premium podcast content and receive
          payouts.
        </p>
        <Button
          type="button"
          variant="primary"
          onClick={handleConnect}
          loading={connecting}
          loadingText="Redirecting..."
        >
          Connect Stripe Account
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <h2>Stripe Connect</h2>
      <div className={styles.statusGrid}>
        <StatusRow label="Account" value={status.stripeAccountId || "—"} />
        <StatusRow label="Charges" connected={status.chargesEnabled} />
        <StatusRow label="Payouts" connected={status.payoutsEnabled} />
        <StatusRow label="Details Submitted" connected={status.detailsSubmitted} />
      </div>

      {(!status.chargesEnabled || !status.payoutsEnabled || !status.detailsSubmitted) && (
        <div className={styles.incompleteNotice}>
          <p>Your account setup is incomplete. Continue onboarding to start accepting payments.</p>
          <Button
            type="button"
            variant="primary"
            onClick={handleConnect}
            loading={connecting}
            loadingText="Redirecting..."
          >
            Continue Setup
          </Button>
        </div>
      )}

      <div className={styles.actions}>
        <a
          href={`https://dashboard.stripe.com/${status.stripeAccountId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.dashboardLink}
        >
          Open Stripe Dashboard ↗
        </a>

        {!showDisconnectConfirm ? (
          <Button type="button" variant="danger" onClick={() => setShowDisconnectConfirm(true)}>
            Disconnect
          </Button>
        ) : (
          <div className={styles.confirmRow}>
            <span className={styles.confirmText}>Are you sure?</span>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowDisconnectConfirm(false)}
              disabled={disconnecting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleDisconnect}
              loading={disconnecting}
              loadingText="Disconnecting..."
            >
              Yes, Disconnect
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusRow({
  label,
  value,
  connected,
}: {
  label: string;
  value?: string;
  connected?: boolean;
}) {
  if (value !== undefined) {
    return (
      <div className={styles.statusRow}>
        <span className={styles.statusLabel}>{label}</span>
        <span className={styles.statusValue}>{value}</span>
      </div>
    );
  }

  return (
    <div className={styles.statusRow}>
      <span className={styles.statusLabel}>{label}</span>
      <span className={connected ? styles.statusEnabled : styles.statusDisabled}>
        {connected ? "✓ Enabled" : "✗ Not enabled"}
      </span>
    </div>
  );
}
